import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
})
export class ChangePasswordComponent extends MenuComponentBase {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private auditLogService = inject(AuditLogService);
  private passwordValidationTimeout?: ReturnType<typeof setTimeout>;
  private confirmValidationTimeout?: ReturnType<typeof setTimeout>;

  private router = inject(Router);
  form: FormGroup = this.fb.group(
    {
      newPassword: ['', [Validators.required, this.passwordStrengthValidator]],
      confirmPassword: ['', Validators.required],
      revokeSessions: [false],
    },
    { validators: this.passwordMatchValidator },
  );

  loading = false;

  showNewPassword = false;
  showConfirmPassword = false;

  showPasswordValidation = false;
  showConfirmValidation = false;

  passwordStrengthValidator(control: AbstractControl) {
    const value: string = control.value || '';
    const errors: Record<string, boolean> = {};

    if (value.length < 8) {
      errors['minLength'] = true;
    }

    if (!/[A-Z]/.test(value)) {
      errors['uppercase'] = true;
    }

    if (!/[a-z]/.test(value)) {
      errors['lowercase'] = true;
    }

    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['special'] = true;
    }

    if (!/[0-9]/.test(value)) {
      errors['number'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  passwordMatchValidator(group: AbstractControl) {
    const newPwd = group.get('newPassword')?.value;
    const confirmPwd = group.get('confirmPassword')?.value;

    return newPwd === confirmPwd ? null : { mismatch: true };
  }

  hasError(rule: string): boolean {
    return !!this.form.get('newPassword')?.errors?.[rule];
  }

  get newPasswordInvalid(): boolean {
    const ctrl = this.form.get('newPassword');

    return !!(this.showPasswordValidation && ctrl?.value && ctrl.invalid);
  }

  get confirmMismatch(): boolean {
    return this.showConfirmValidation;
  }

  onNewPasswordInput(): void {
    this.showPasswordValidation = false;

    clearTimeout(this.passwordValidationTimeout);

    this.passwordValidationTimeout = setTimeout(() => {
      const ctrl = this.form.get('newPassword');

      this.showPasswordValidation = !!(ctrl?.value && ctrl.invalid);

      this.form.get('confirmPassword')?.updateValueAndValidity();
    }, 700);
  }

  onConfirmPasswordInput(): void {
    this.showConfirmValidation = false;

    clearTimeout(this.confirmValidationTimeout);

    this.confirmValidationTimeout = setTimeout(() => {
      const ctrl = this.form.get('confirmPassword');

      this.showConfirmValidation = !!(
        ctrl?.value && this.form.errors?.['mismatch']
      );
    }, 700);
  }

  revokeSessions(): void {
    this.auditLogService.logFeature({
      featureName: 'revoke_sessions',
      action: 'revoke',
      meta: {
        userId: 'current_user',
        isAdminAction: 'false',
        changedPassword: 'true',
      },
      description: 'User revokes all sessions after changing password',
    });

    this.authService
      .revokeAllSessions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.authService.clearLocalStore();
          this.router.navigate(['/login']);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 403) {
            this.commonService.triggerToast({
              type: ToastType.Error,
              message:
                'You do not have permissions to revoke sessions for this user.',
            });
          } else {
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: 'Failed to revoke sessions. Please try again.',
            });
          }
        },
      });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    const { newPassword, revokeSessions } = this.form.getRawValue();

    this.authService
      .changePassword(newPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Password changed successfully.',
          });
          if (revokeSessions) {
            this.revokeSessions();
          } else {
            this.router.navigate(['/dashboard']);
          }
        },
        error: (err) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to change password. Please try again.',
          });
          console.error(err.error?.error);
          this.loading = false;
        },
      });
  }
}
